import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  Fade,
  Stack,
  useTheme,
  alpha,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  DataSaverOff as DataOffIcon,
  PictureAsPdf as PdfIcon,
  FileDownload as DownloadIcon,
  TableView as ExcelIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import CheckingReport from './CheckingReport';
import { format, parseISO, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import * as XLSX from 'xlsx';

import RegistrosService, {
  Registro,
  RegistroFilter as BaseRegistroFilter,
  AudioLink,
} from '../../services/registrosService';
import AcessosService, { UserAcessos } from '../../services/acessosService';
import EmissorasService from '../../services/emissorasService';
import MarcasService from '../../services/marcasService';
import ItensService from '../../services/itensService';
import SetoresService from '../../services/setoresService';
import ProgramasService from '../../services/programasService';
import AgenciasService from '../../services/agenciasService';
import AnunciantesService from '../../services/anunciantesService';
import CategoriasService from '../../services/categoriasService';

// Estendendo a interface RegistroFilter para incluir campos adicionais necessários
interface RegistroFilter extends BaseRegistroFilter {
  item_id?: number;
  setor_id?: number;
  agencia_id?: number;
  categoria_id?: number;
  emissora_ids?: number[];
  cidades?: string[];
  estados?: string[];
  duracao?: number;
  duracao_min?: number;
  duracao_max?: number;
  pi_number?: string;
}

// Lista de tipos de registro (copiado de RegistrosPage.tsx)
const TIPOS_REGISTRO = [
  {id: 'A', nome: 'A - Inicio/Encerramento Controle'},
  {id: 'a', nome: 'a - Patrocício de Vinheta de Programa'},
  {id: 'N', nome: 'N - Comercial'},
  {id: 'M', nome: 'M - Merchandising'},
  {id: 'P', nome: 'P - Programa'},
  {id: 'p', nome: 'p - Patrocínio de Vinheta de Passagem'},
  {id: 'I', nome: 'I - Interprograma'},
  {id: 'V', nome: 'V - Vinheta'},
  {id: 'v', nome: 'v - Patrocínio de Vinheta'},
  {id: 'X', nome: 'X - Eventos'},
  {id: 'x', nome: 'x - Patrocínio de Eventos'},
  {id: 'Z', nome: 'Z - Comercial Político'},
  {id: 'C', nome: 'C - Chamada'},
  {id: 'c', nome: 'c - Patrocínio de Chamada'},
  {id: 'B', nome: 'B - Break'},
  {id: 'b', nome: 'b - Patrocínio de Boletim'},
  {id: 'T', nome: 'T - Top'},
  {id: 't', nome: 't - Patrocínio de Top'},
  {id: 'S', nome: 'S - Subprograma'},
  {id: 'Y', nome: 'Y - Encerramento de Subprograma'},
  {id: 'Q', nome: 'Q - Quadro'},
  {id: 'E', nome: 'E - Encerramento do Quadro'},
  {id: 'e', nome: 'e - Patrocínio de Encerramento de Programa'},
  {id: 'F', nome: 'F - Falha Técnica de Controle'},
  {id: 'R', nome: 'R - Retorno de Falha Técnica'},
  {id: 'q', nome: 'q - Patrocínio de Vinheta de Quadro'}
];

// Interface para agrupar registros por emissora
interface RegistroAgrupado {
  emissoraNome: string;
  registros: Registro[];
  totalDuracao: number;
  totalCusto: number;
  totalComerciais: number;
}

// Interface para agrupar registros por emissora, periodo e programa (para o relatório de exibição)
interface RegistroExibicao {
  emissoraNome: string;
  periodo: string; // formato MM/YYYY
  programaNome: string;
  programa_id: number;
  duracao: number;
  custoUnitario: number;
  diasExibicao: Record<number, number>; // dia -> quantidade
  total: number;
}

// Tipo de relatório
type TipoRelatorio = 'checking' | 'exibicao';

const RelatoriosPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // Estado de tipo de relatório
  const [tipoRelatorio, setTipoRelatorio] = useState<TipoRelatorio>('checking');
  
  // Estado de acessos do usuário
  const [acessos, setAcessos] = useState<UserAcessos>({
    emissoras: [],
    marcas: [],
    itens: [],
    setores: []
  });
  
  // Estado de listagem
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [registrosAgrupados, setRegistrosAgrupados] = useState<RegistroAgrupado[]>([]);
  const [registrosExibicao, setRegistrosExibicao] = useState<RegistroExibicao[]>([]);
  const [periodosExibicao, setPeriodosExibicao] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  // Links do áudio da rádio por registro (só existe para emissoras de rádio gravadas)
  const [audioLinks, setAudioLinks] = useState<Record<string, AudioLink>>({});
  const [totalDuracao, setTotalDuracao] = useState(0);
  const [totalCusto, setTotalCusto] = useState(0);
  const [totalComerciais, setTotalComerciais] = useState(0);
  
  // Estado de filtros
  const [filter, setFilter] = useState<RegistroFilter>({});
  const [qualidadesDisponiveis, setQualidadesDisponiveis] = useState<string[]>([]);
  const [generosDisponiveis, setGenerosDisponiveis] = useState<string[]>([]);
  const [emissorasDisponiveis, setEmissorasDisponiveis] = useState<Array<{id: number, nome: string, cidade?: string, estado?: string}>>([]);
  const [cidadesDisponiveis, setCidadesDisponiveis] = useState<string[]>([]);
  const [estadosDisponiveis, setEstadosDisponiveis] = useState<string[]>([]);
  const [marcasDisponiveis, setMarcasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [itensDisponiveis, setItensDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [setoresDisponiveis, setSetoresDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [programasDisponiveis, setProgramasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [agenciasDisponiveis, setAgenciasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [anunciantesDisponiveis, setAnunciantesDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  // Usando a lista estática de tipos para garantir consistência com RegistrosPage
  const [tiposDisponiveis, setTiposDisponiveis] = useState<Array<{id: string, nome: string}>>(TIPOS_REGISTRO);
  const [mercadosDisponiveis, setMercadosDisponiveis] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [mostrarCustos, setMostrarCustos] = useState(true);
  // Checking: agrupar por campanha no PDF (padrão = lista corrida) + observação no fim do relatório
  const [agruparCampanhas, setAgruparCampanhas] = useState(false);
  const [observacaoRelatorio, setObservacaoRelatorio] = useState('');
  const [incluirGraficos, setIncluirGraficos] = useState(false);
  // Áudio da inserção no relatório. Desligado por padrão: o link é público (quem
  // recebe o PDF ouve sem login), então quem monta o relatório decide expor.
  const [incluirAudio, setIncluirAudio] = useState(false);

  // Dados dos gráficos (volume diário + distribuição por horário) a partir dos registros filtrados
  const graficosRelatorio = useMemo(() => {
    const diaMap: Record<string, { label: string; count: number }> = {};
    const horaMap: Record<number, number> = {};
    registros.forEach(r => {
      if (!r.datahora) return;
      const iso = r.datahora.substring(0, 10);
      if (!diaMap[iso]) diaMap[iso] = { label: `${iso.substring(8, 10)}/${iso.substring(5, 7)}`, count: 0 };
      diaMap[iso].count += 1;
      const h = parseInt(r.datahora.substring(11, 13), 10);
      if (!isNaN(h)) horaMap[h] = (horaMap[h] || 0) + 1;
    });
    const dailyData = Object.keys(diaMap).sort().map(iso => ({ formattedDate: diaMap[iso].label, count: diaMap[iso].count }));
    const hourlyData: { hour: string; count: number }[] = [];
    for (let h = 0; h < 24; h++) if (horaMap[h]) hourlyData.push({ hour: `${String(h).padStart(2, '0')}h`, count: horaMap[h] });
    return { dailyData, hourlyData };
  }, [registros]);

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  
  // Carregar dados de acessos do usuário
  const carregarAcessos = useCallback(async () => {
    try {
      console.log('Buscando acessos do usuário para relatórios...');
      const dadosAcessos = await AcessosService.getUserAcessos();
      setAcessos(dadosAcessos);
      console.log('Acessos carregados:', dadosAcessos);
    } catch (error) {
      console.error('Erro ao carregar acessos do usuário:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar acessos do usuário',
        severity: 'error',
      });
    }
  }, []);

  // Carregar dados de filtros
  const fetchFilterData = useCallback(async () => {
    try {
      console.log('Buscando dados de filtros para relatórios...', acessos);
      
      // Buscar dados em paralelo, mas com tratamento de erros individual para cada promessa
      const emissorasPromise = EmissorasService.getEmissoras({}, 0, 1000);
      const itensPromise = ItensService.getAllItens();
      const setoresPromise = SetoresService.getSetores({}, 0, 1000);
      const programasPromise = ProgramasService.getAllProgramas();
      const agenciasPromise = AgenciasService.getAllAgencias();
      const anunciantesPromise = AnunciantesService.getAllAnunciantes();
      const categoriasPromise = CategoriasService.getAllCategorias();
      const marcasPromise = MarcasService.getMarcas({}, 0, 50000); // Aumentando o limite para capturar todas as marcas
      
      console.log('Iniciando carregamento de programas...');
      
      // Tratar os endpoints com falha 422 separadamente usando Promise.allSettled
      const metadata = await Promise.allSettled([
        { name: 'qualidades', promise: RegistrosService.getQualidades() },
        { name: 'generos', promise: RegistrosService.getGeneros() },
        // { name: 'tipos', promise: RegistrosService.getTipos() }, // Usando lista estática
        { name: 'mercados', promise: RegistrosService.getMercados() }
      ].map(async item => {
        try {
          const result = await item.promise;
          return { name: item.name, data: result, success: true };
        } catch (error) {
          console.warn(`Erro ao buscar ${item.name}:`, error);
          return { name: item.name, data: [], success: false };
        }
      }));
      
      // Processar os resultados da metadata
      metadata.forEach(result => {
        if (result.status === 'fulfilled') {
          const item = result.value;
          if (item.success) {
            switch(item.name) {
              case 'qualidades':
                setQualidadesDisponiveis(item.data || []);
                break;
              case 'generos':
                setGenerosDisponiveis(item.data || []);
                break;
              /* case 'tipos':
                setTiposDisponiveis(item.data || []);
                break; */
              case 'mercados':
                setMercadosDisponiveis(item.data || []);
                break;
            }
          }
        }
      });
      
      // Processar dados de emissoras e outros dados básicos
      try {
        const [
          emissoras, 
          itens, 
          setores, 
          programas, 
          agencias, 
          anunciantes, 
          categorias,
          marcas
        ] = await Promise.all([
          emissorasPromise,
          itensPromise,
          setoresPromise,
          programasPromise,
          agenciasPromise,
          anunciantesPromise,
          categoriasPromise,
          marcasPromise
        ]);
        
        // Buscar estados e cidades disponíveis das emissoras
        const emissorasArray = Array.isArray(emissoras) ? emissoras : [];
        const cidadesSet = new Set<string>();
        const estadosSet = new Set<string>();
        
        emissorasArray.forEach(emissora => {
          if (emissora.cidade) cidadesSet.add(emissora.cidade);
          if (emissora.estado) estadosSet.add(emissora.estado);
        });
        
        setCidadesDisponiveis(Array.from(cidadesSet).sort());
        setEstadosDisponiveis(Array.from(estadosSet).sort());

        // Filtrar os dados disponíveis com base nos acessos do usuário
        const itensArray = Array.isArray(itens) ? itens : [];
        const setoresArray = Array.isArray(setores) ? setores : [];
        const programasArray = Array.isArray(programas) ? programas : [];
        const agenciasArray = Array.isArray(agencias) ? agencias : [];
        const anunciantesArray = Array.isArray(anunciantes) ? anunciantes : [];
        const categoriasArray = Array.isArray(categorias) ? categorias : [];
        const marcasArray = Array.isArray(marcas) ? marcas : [];
        
        // Logs para depuração
        console.log('Acessos do usuário:', acessos);
        console.log('Emissoras recebidas da API:', emissorasArray.length);
        console.log('Marcas recebidas da API:', marcasArray.length);
        console.log('Itens recebidos da API:', itensArray.length);
        console.log('Setores recebidos da API:', setoresArray.length);
        console.log('Programas recebidos da API:', programasArray.length);
        
        // Verificar se o usuário é admin
        const isAdmin = user?.is_admin;
        console.log('Usuário é admin?', isAdmin);
        
        if (isAdmin) {
          // Se for admin, mostrar todos os dados
          setEmissorasDisponiveis(emissorasArray);
          setItensDisponiveis(itensArray);
          setSetoresDisponiveis(setoresArray);
          setProgramasDisponiveis(programasArray);
          setAgenciasDisponiveis(agenciasArray);
          setAnunciantesDisponiveis(anunciantesArray);
          setCategoriasDisponiveis(categoriasArray);
          setMarcasDisponiveis(marcasArray);
          
          console.log('Admin: Mostrando todas as opções');
        } else {
          // Filtrar com base nos acessos do usuário
          const emissorasFiltradas = emissorasArray.filter(e => acessos.emissoras.includes(e.id));
          const itensFiltrados = itensArray.filter(i => acessos.itens.includes(i.id));
          const setoresFiltrados = setoresArray.filter(s => acessos.setores.includes(s.id));
          const marcasFiltradas = marcasArray.filter(m => acessos.marcas.includes(m.id));
          
          console.log('Emissoras filtradas:', emissorasFiltradas.length, 'de', emissorasArray.length);
          console.log('Marcas filtradas:', marcasFiltradas.length, 'de', marcasArray.length);
          console.log('Itens filtrados:', itensFiltrados.length, 'de', itensArray.length);
          console.log('Setores filtrados:', setoresFiltrados.length, 'de', setoresArray.length);
          
          // Garantir que os arrays filtrados tenham pelo menos um item
          if (emissorasFiltradas.length === 0 && acessos.emissoras.length > 0) {
            console.warn('Aviso: Não foi possível encontrar emissoras correspondentes aos IDs de acesso');
          }
          if (marcasFiltradas.length === 0 && acessos.marcas.length > 0) {
            console.warn('Aviso: Não foi possível encontrar marcas correspondentes aos IDs de acesso');
          }
          if (itensFiltrados.length === 0 && acessos.itens.length > 0) {
            console.warn('Aviso: Não foi possível encontrar itens correspondentes aos IDs de acesso');
          }
          if (setoresFiltrados.length === 0 && acessos.setores.length > 0) {
            console.warn('Aviso: Não foi possível encontrar setores correspondentes aos IDs de acesso');
          }
          
          setEmissorasDisponiveis(emissorasFiltradas);
          setItensDisponiveis(itensFiltrados);
          setSetoresDisponiveis(setoresFiltrados);
          setMarcasDisponiveis(marcasFiltradas);
          
          // Outros filtros que talvez precisem ser relacionados com base nos IDs disponíveis
          setProgramasDisponiveis(programasArray);
          setAgenciasDisponiveis(agenciasArray);
          setAnunciantesDisponiveis(anunciantesArray);
          setCategoriasDisponiveis(categoriasArray);
        }
      } catch (mainDataError) {
        console.error('Erro ao carregar dados principais:', mainDataError);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de filtros:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de filtros',
        severity: 'error',
      });
    }
  }, [acessos, user?.is_admin]);

  // Verificar se os programas foram carregados corretamente
  useEffect(() => {
    if (programasDisponiveis.length > 0) {
      console.log('Programas disponíveis para seleção:', programasDisponiveis.slice(0, 5));
    }
  }, [programasDisponiveis]);

  // Agrupar registros para o relatório de exibição
  const agruparRegistrosExibicao = useCallback((registrosFiltrados: Registro[]) => {
    // Agrupar por emissora, período (mês/ano) e programa
    const registrosAgrupados: Record<string, Record<string, Record<string, RegistroExibicao>>> = {};
    const periodosSet = new Set<string>();
    
    // Processar cada registro
    registrosFiltrados.forEach(registro => {
      if (!registro.datahora || !registro.emissora_id) return;
      
      // Obter a emissora
      const emissora = emissorasDisponiveis.find(e => e.id === registro.emissora_id);
      if (!emissora) return;
      
      // Formatar o período (mês/ano)
      const date = parseISO(registro.datahora);
      const periodo = format(date, 'MM/yyyy');
      periodosSet.add(periodo);
      
      // Obter o dia do mês
      const dia = parseInt(format(date, 'd'));
      
      // Obter o programa
      const programa = programasDisponiveis.find(p => p.id === registro.programa_id);
      const programaNome = programa?.nome || 'Sem Programa';
      const programa_id = registro.programa_id || 0;
      
      // Criar chaves para agrupamento
      const emissoraKey = emissora.nome;
      
      // Inicializar objetos se necessário
      if (!registrosAgrupados[emissoraKey]) {
        registrosAgrupados[emissoraKey] = {};
      }
      
      if (!registrosAgrupados[emissoraKey][periodo]) {
        registrosAgrupados[emissoraKey][periodo] = {};
      }
      
      const programaKey = `${programa_id}-${programaNome}`;
      
      if (!registrosAgrupados[emissoraKey][periodo][programaKey]) {
        registrosAgrupados[emissoraKey][periodo][programaKey] = {
          emissoraNome: emissoraKey,
          periodo: periodo,
          programaNome: programaNome,
          programa_id: programa_id,
          duracao: registro.duracao || 0,
          custoUnitario: registro.custo || 0,
          diasExibicao: {},
          total: 0
        };
      }
      
      // Incrementar o contador para o dia específico
      const agrupado = registrosAgrupados[emissoraKey][periodo][programaKey];
      agrupado.diasExibicao[dia] = (agrupado.diasExibicao[dia] || 0) + 1;
      agrupado.total += 1;
    });
    
    // Converter para array e ordenar
    const resultado: RegistroExibicao[] = [];
    
    // Ordenar períodos
    const periodos = Array.from(periodosSet).sort();
    setPeriodosExibicao(periodos);
    
    // Para cada emissora
    Object.keys(registrosAgrupados).sort().forEach(emissoraNome => {
      // Para cada período
      periodos.forEach(periodo => {
        if (registrosAgrupados[emissoraNome][periodo]) {
          // Para cada programa
          Object.values(registrosAgrupados[emissoraNome][periodo])
            .sort((a, b) => a.programaNome.localeCompare(b.programaNome))
            .forEach(programa => {
              resultado.push(programa);
            });
        }
      });
    });
    
    setRegistrosExibicao(resultado);
  }, [emissorasDisponiveis, programasDisponiveis]);

  // Buscar registros com base nos filtros aplicados
  const fetchRegistros = useCallback(async () => {
    try {
      setLoading(true);
      
      // Se não for admin, adicione os filtros de acesso
      const filterComAcessos = { ...filter };
      
      // Filtrar emissoras por cidade e estado antes de buscar os registros
      let emissorasFiltradasPorRegiao = emissorasDisponiveis;
      
      // Aplicar filtros de cidade e estado
      if (filter.cidades && filter.cidades.length > 0) {
        emissorasFiltradasPorRegiao = emissorasFiltradasPorRegiao.filter(e => 
          e.cidade && filter.cidades?.includes(e.cidade)
        );
      }
      
      if (filter.estados && filter.estados.length > 0) {
        emissorasFiltradasPorRegiao = emissorasFiltradasPorRegiao.filter(e => 
          e.estado && filter.estados?.includes(e.estado)
        );
      }
      
      // Se temos filtros de região, aplicar os IDs das emissoras filtradas
      // Independente se já existem emissoras selecionadas ou não
      if ((filter.cidades && filter.cidades.length > 0) || (filter.estados && filter.estados.length > 0)) {
        // Se já tem emissoras selecionadas, filtrar apenas dentro dessas emissoras
        if (filter.emissora_ids && filter.emissora_ids.length > 0) {
          // Filtrar apenas as emissoras que estão tanto na lista de IDs quanto na lista filtrada por região
          const idsEmissorasRegiao = emissorasFiltradasPorRegiao.map(e => e.id);
          filterComAcessos.emissora_ids = filter.emissora_ids.filter(id => idsEmissorasRegiao.includes(id));
          
          // Se não sobrar nenhuma emissora, usar todas da região
          if (filterComAcessos.emissora_ids.length === 0) {
            filterComAcessos.emissora_ids = idsEmissorasRegiao;
          }
        } 
        // Se tem filtro de emissora_id singular, verificar se está na região
        else if (filter.emissora_id) {
          const emissoraEstaNaRegiao = emissorasFiltradasPorRegiao.some(e => e.id === filter.emissora_id);
          if (!emissoraEstaNaRegiao) {
            // Se a emissora não está na região, usar todas as emissoras da região
            filterComAcessos.emissora_id = undefined;
            filterComAcessos.emissora_ids = emissorasFiltradasPorRegiao.map(e => e.id);
          }
        }
        // Se não tem nenhuma emissora selecionada, usar todas da região
        else {
          filterComAcessos.emissora_ids = emissorasFiltradasPorRegiao.map(e => e.id);
        }
        
        // Armazenar os IDs das emissoras filtradas por região para uso posterior
        const emissorasFiltradasIds = emissorasFiltradasPorRegiao.map(e => e.id);
        console.log(`Emissoras filtradas por região: ${emissorasFiltradasIds.length}`, emissorasFiltradasIds);
      }
      
      // Formatar corretamente as datas para a API (adicionando o separador 'T' e componente de tempo)
      if (filterComAcessos.datahora_inicio) {
        filterComAcessos.datahora_inicio = formatDateForApi(filterComAcessos.datahora_inicio, false);
      }
      
      if (filterComAcessos.datahora_fim) {
        filterComAcessos.datahora_fim = formatDateForApi(filterComAcessos.datahora_fim, true);
      }
      
      if (!user?.is_admin) {
        // Aplicar filtros de acesso ao enviar para a API (para otimizar resultados)
        // Verificar se os filtros atuais respeitam as permissões do usuário
        if (filterComAcessos.emissora_id && !acessos.emissoras.includes(filterComAcessos.emissora_id)) {
          filterComAcessos.emissora_id = acessos.emissoras.length > 0 ? acessos.emissoras[0] : undefined;
        }
        
        // Verificar emissora_ids (múltipla seleção)
        if (filterComAcessos.emissora_ids && filterComAcessos.emissora_ids.length > 0) {
          // Filtrar apenas as emissoras que o usuário tem acesso
          filterComAcessos.emissora_ids = filterComAcessos.emissora_ids.filter(id => acessos.emissoras.includes(id));
          if (filterComAcessos.emissora_ids.length === 0) {
            filterComAcessos.emissora_ids = undefined;
          }
        }
        
        if (filterComAcessos.marca_id && !acessos.marcas.includes(filterComAcessos.marca_id)) {
          filterComAcessos.marca_id = acessos.marcas.length > 0 ? acessos.marcas[0] : undefined;
        }
        
        if (filterComAcessos.item_id && !acessos.itens.includes(filterComAcessos.item_id)) {
          filterComAcessos.item_id = acessos.itens.length > 0 ? acessos.itens[0] : undefined;
        }
        
        if (filterComAcessos.setor_id && !acessos.setores.includes(filterComAcessos.setor_id)) {
          filterComAcessos.setor_id = acessos.setores.length > 0 ? acessos.setores[0] : undefined;
        }
        
        // Removemos a seleção automática da primeira emissora
        // Para não restringir os resultados desnecessariamente
        // Vamos deixar a API retornar todos os registros e filtrar no cliente
      }
      
      console.log('[RelatoriosPage] Enviando filtros para API:', filterComAcessos);
      
      // Garantir que as emissoras estejam carregadas antes de prosseguir
      let emissorasAtualizadas = emissorasDisponiveis;
      
      // Se não temos emissoras carregadas, vamos buscar elas novamente
      if (emissorasDisponiveis.length === 0) {
        try {
          console.log('Carregando emissoras antes de agrupar registros...');
          const emissoras = await EmissorasService.getEmissoras({}, 0, 1000);
          const emissorasArray = Array.isArray(emissoras) ? emissoras : [];
          
          // Atualizar o estado somente após a pesquisa atual
          if (emissorasArray.length > 0) {
            console.log(`Carregadas ${emissorasArray.length} emissoras para agrupamento`);
            emissorasAtualizadas = user?.is_admin 
              ? emissorasArray 
              : emissorasArray.filter(e => acessos.emissoras.includes(e.id));
            
            // Atualizar o estado global de emissoras disponíveis
            setEmissorasDisponiveis(emissorasAtualizadas);
          }
        } catch (error) {
          console.error('Erro ao carregar emissoras para agrupamento:', error);
        }
      }
      
      // Buscar todos os registros que correspondem aos filtros (sem paginação para relatórios).
      // PI e duração agora vão no filtro da API (SQL); antes eram filtrados só em
      // memória e o teto abaixo cortava silenciosamente os registros mais antigos.
      // Busca em blocos de 10 mil para não montar uma resposta gigante de uma vez.
      const LIMITE_REGISTROS = 50000;
      const registrosData = await RegistrosService.getRegistrosEmBlocos(filterComAcessos, LIMITE_REGISTROS, 10000);

      // O teto de linhas não pode mais cortar sem avisar: se veio exatamente o
      // limite, existem registros mais antigos que não chegaram na tela.
      if (Array.isArray(registrosData) && registrosData.length >= LIMITE_REGISTROS) {
        setSnackbar({
          open: true,
          message: `A pesquisa atingiu o limite de ${LIMITE_REGISTROS.toLocaleString('pt-BR')} registros e os mais antigos ficaram de fora. Reduza o período ou use mais filtros (emissora, marca, PI).`,
          severity: 'warning',
        });
      }

      // Armazenar os IDs das emissoras filtradas por região para filtrar registros
      const emissorasFiltradasIds = emissorasFiltradasPorRegiao.map(e => e.id);
      
      // Filtrar registros primeiro por permissões e depois por região (cidade/estado) e duração
      const registrosFiltrados = registrosData.filter((registro: Registro) => {
        // Verificar se o registro pertence a uma emissora da região filtrada
        const passaFiltroRegiao = !((filter.cidades && filter.cidades.length > 0) || 
                                    (filter.estados && filter.estados.length > 0)) || 
                                 !registro.emissora_id || 
                                 emissorasFiltradasIds.includes(registro.emissora_id);
        
        // Se não passa no filtro de região, já descarta
        if (!passaFiltroRegiao) {
          return false;
        }

        // Filtro de Duração (faixa em segundos, limites inclusivos)
        if (filter.duracao_min != null || filter.duracao_max != null) {
          const duracaoRegistro = registro.duracao;
          if (duracaoRegistro == null) {
            return false;
          }
          if (filter.duracao_min != null && duracaoRegistro < filter.duracao_min) {
            return false;
          }
          if (filter.duracao_max != null && duracaoRegistro > filter.duracao_max) {
            return false;
          }
        }

        // Filtro de PI Number
        if (filter.pi_number !== undefined && filter.pi_number !== null && filter.pi_number !== '') {
          // Verifica se o pi_number do registro contém o valor do filtro (case insensitive)
          if (!registro.pi_number || !registro.pi_number.toLowerCase().includes(filter.pi_number.toLowerCase())) {
            return false;
          }
        }
        
        // Se não for admin, verificar permissões de acesso
        if (!user?.is_admin) {
        // Verificar se o usuário tem acesso à emissora do registro
        const temAcessoEmissora = !registro.emissora_id || acessos.emissoras.includes(registro.emissora_id);
        
        // Verificar se o usuário tem acesso à marca do registro
        const temAcessoMarca = !registro.marca_id || acessos.marcas.includes(registro.marca_id);
        
        // Verificar se o usuário tem acesso ao item do registro
        const temAcessoItem = !registro.item_id || acessos.itens.includes(registro.item_id);
        
        // Verificar se o usuário tem acesso ao setor do registro
        const temAcessoSetor = !registro.setor_id || acessos.setores.includes(registro.setor_id);
        
        // O registro só é visível se o usuário tiver acesso a todos os seus campos
        return temAcessoEmissora && temAcessoMarca && temAcessoItem && temAcessoSetor;
        }
        
        // Se for admin ou tiver todas as permissões necessárias
        return true;
      });

      // Preenche o custo com a TARIFA DA EMISSORA (emissora.valor) quando o registro
      // não tem custo próprio — assim o valor aparece nas linhas, totais e no PDF.
      const valorPorEmissora: Record<number, number> = {};
      emissorasDisponiveis.forEach((e: any) => { if (e && e.valor) valorPorEmissora[e.id] = e.valor; });
      registrosFiltrados.forEach((r: Registro) => {
        if ((!r.custo || r.custo === 0) && r.emissora_id && valorPorEmissora[r.emissora_id]) {
          r.custo = valorPorEmissora[r.emissora_id];
        }
      });

      console.log(`Registros filtrados: ${registrosFiltrados.length} de ${registrosData.length} (filtrados por permissões de acesso)`);
      setRegistros(registrosFiltrados);
      
      // Agrupar registros para o relatório de exibição
      if (tipoRelatorio === 'exibicao') {
        agruparRegistrosExibicao(registrosFiltrados);
      }
      
      // Calcular totais
      let duracaoTotal = 0;
      let custoTotal = 0;
      
      // Agrupar por emissora
      const registrosPorEmissora: Record<string, RegistroAgrupado> = {};
      
      // Se há um filtro de emissora selecionado, busca o nome da emissora para usar nos registros sem emissora
      let emissoraSelecionadaNome = 'Sem Emissora';
      if (filter.emissora_id) {
        const emissoraSelecionada = emissorasAtualizadas.find(e => e.id === filter.emissora_id);
        if (emissoraSelecionada) {
          emissoraSelecionadaNome = emissoraSelecionada.nome;
        }
      } else if (filter.emissora_ids && filter.emissora_ids.length === 1) {
        const emissoraSelecionada = emissorasAtualizadas.find(e => e.id === filter.emissora_ids[0]);
        if (emissoraSelecionada) {
          emissoraSelecionadaNome = emissoraSelecionada.nome;
        }
      } else if (filter.emissora_ids && filter.emissora_ids.length > 1) {
        emissoraSelecionadaNome = 'Múltiplas Emissoras';
      }
      
      // Log para diagnóstico - verificar se temos as emissoras carregadas
      console.log(`Agrupando ${registrosFiltrados.length} registros com ${emissorasAtualizadas.length} emissoras disponíveis`);
      
      console.log(`Registros após filtro de região: ${registrosFiltrados.length}`);
      if (filter.estados && filter.estados.length > 0) {
        console.log(`Filtro de estados aplicado: ${filter.estados.join(', ')}`);
      }
      if (filter.cidades && filter.cidades.length > 0) {
        console.log(`Filtro de cidades aplicado: ${filter.cidades.join(', ')}`);
      }
      
      // Usar os registros filtrados (respeitando permissões de acesso e região) ao invés dos registros originais
      registrosFiltrados.forEach((registro: Registro) => {
        // Buscar o nome da emissora no array de emissoras disponíveis usando o emissora_id do registro
        let emissoraNome = 'Sem Emissora';
        
        if (registro.emissora_id) {
          // Buscar a emissora pelo ID nas emissoras atualizadas
          const emissora = emissorasAtualizadas.find(e => e.id === registro.emissora_id);
          if (emissora) {
            emissoraNome = emissora.nome;
          } else {
            console.log(`Não foi possível encontrar nome para emissora ID: ${registro.emissora_id}`);
          }
          
          // Removida verificação redundante de filtro de múltiplas emissoras
          // Esta verificação estava causando bug onde registros válidos eram descartados
          // A API já retorna apenas os registros que correspondem aos filtros enviados
        } else if (filter.emissora_id || (filter.emissora_ids?.length ?? 0) > 0) {
          // Se não tiver emissora mas houver filtro de emissora, usar a emissora selecionada
          emissoraNome = emissoraSelecionadaNome;
        }
        const duracao = registro.duracao || 0;
        const custo = registro.custo || 0;
        
        duracaoTotal += duracao;
        custoTotal += custo;
        
        if (!registrosPorEmissora[emissoraNome]) {
          registrosPorEmissora[emissoraNome] = {
            emissoraNome,
            registros: [],
            totalDuracao: 0,
            totalCusto: 0,
            totalComerciais: 0
          };
        }
        
        registrosPorEmissora[emissoraNome].registros.push(registro);
        registrosPorEmissora[emissoraNome].totalDuracao += duracao;
        registrosPorEmissora[emissoraNome].totalCusto += custo;
        registrosPorEmissora[emissoraNome].totalComerciais += 1;
      });
      
      // Converter para array e ordenar por nome da emissora
      const registrosAgrupados = Object.values(registrosPorEmissora).sort((a, b) => 
        a.emissoraNome.localeCompare(b.emissoraNome)
      );
      
      setRegistrosAgrupados(registrosAgrupados);
      setTotalDuracao(duracaoTotal);
      setTotalCusto(custoTotal);
      setTotalComerciais(registrosFiltrados.length);
      
    } catch (error) {
      console.error('Erro ao buscar registros para relatório:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar registros para relatório',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filter, user?.is_admin, acessos, emissorasDisponiveis, tipoRelatorio, agruparRegistrosExibicao]);

  // Função para formatar data como YYYY-MM-DDT00:00:00 para os campos de data
  const formatDateForInput = (date: Date): string => {
    // Para inputs visuais na UI, mantemos o formato simples yyyy-MM-dd
    return format(date, 'yyyy-MM-dd');
  };
  
  // Função para formatar data para a API, incluindo separador T e componente de hora
  const formatDateForApi = (dateStr: string, isEndDate: boolean = false): string => {
    // Se já estiver no formato com T, retorna como está
    if (dateStr.includes('T')) return dateStr;
    
    // Para datas de fim, use o final do dia (23:59:59) para incluir todos os registros do dia
    // Para datas de início, use o início do dia (00:00:00)
    return isEndDate ? `${dateStr}T23:59:59` : `${dateStr}T00:00:00`;
  };
  
  // Definir datas padrão ao iniciar
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = subDays(today, 7);
    
    setFilter(prev => ({
      ...prev,
      datahora_fim: formatDateForInput(today),
      datahora_inicio: formatDateForInput(sevenDaysAgo)
    }));
  }, []);
  
  useEffect(() => {
    // Buscar acessos do usuário ao iniciar
    carregarAcessos();
  }, [carregarAcessos]);

  // A coluna de áudio só aparece se a opção estiver marcada E algum registro da
  // lista tiver gravação
  const temAudioDisponivel = useMemo(
    () => incluirAudio && registros.some(r => audioLinks[String(r.id)]?.disponivel),
    [incluirAudio, registros, audioLinks],
  );

  // Busca os links de áudio dos registros carregados (uma chamada para a lista
  // inteira). Só roda com a opção ligada; registros de TV voltam como
  // indisponíveis e a coluna nem aparece no relatório.
  useEffect(() => {
    let cancelado = false;
    if (!incluirAudio || registros.length === 0) {
      setAudioLinks({});
      return;
    }
    RegistrosService.getAudioLinks(registros.map(r => r.id))
      .then(links => { if (!cancelado) setAudioLinks(links); })
      .catch(err => {
        console.error('[Relatorios] Falha ao buscar links de áudio:', err);
        if (!cancelado) setAudioLinks({});
      });
    return () => { cancelado = true; };
  }, [incluirAudio, registros]);
  
  useEffect(() => {
    // Buscar dados de filtros após ter os acessos carregados
    if (acessos.emissoras.length > 0 || acessos.marcas.length > 0 || 
        acessos.itens.length > 0 || acessos.setores.length > 0 || user?.is_admin) {
      fetchFilterData();
    }
  }, [fetchFilterData, acessos, user]);

  // Handlers para filtros
  const handleFilterChange = (field: keyof RegistroFilter, value: any) => {
    setFilter((prev: RegistroFilter) => ({
      ...prev,
      [field]: value || undefined, // Usar undefined para remover o filtro vazio
    }));
  };

  const handleClearFilters = () => {
    setFilter({});
  };

  const handleSearch = () => {
    fetchRegistros();
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Função para exportar o relatório como PDF usando window.print()
  const exportToPDF = () => {
    if (registrosAgrupados.length === 0) {
      setSnackbar({
        open: true,
        message: 'Não há dados para exportar',
        severity: 'error',
      });
      return;
    }

    try {
      // Salvar o estilo original do body
      const originalBodyStyle = document.body.style.cssText;
      
      // Configurar a página para impressão
      const printStyles = document.createElement('style');
      printStyles.innerHTML = `
        @media print {
          body * { visibility: hidden; }
          #relatorio, #relatorio * {
            visibility: visible;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* O logo fica escondido na tela (display:none) e só aparece na impressão/PDF */
          #relatorio .relatorio-logo-print { display: flex !important; }
          /* Checking: tela mostra a tabela de sempre; no PDF mostra o layout novo */
          #relatorio .checking-tela { display: none !important; }
          #relatorio .checking-pdf { display: block !important; }
          #relatorio {
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%;
            /* Ajustar escala para caber em A4 */
            zoom: 0.75;
          }
          
          /* Ajustes específicos para tabelas na impressão */
          #relatorio .MuiTableCell-root {
            padding: 4px 4px !important;
            font-size: 0.7rem !important;
            line-height: 1.2 !important;
          }
          
          #relatorio .MuiTypography-h5 {
            font-size: 1.2rem !important;
          }
          
          #relatorio .MuiTypography-h6 {
            font-size: 1rem !important;
          }
          
          /* Checking (novo layout) em retrato; Exibição em paisagem */
          @page { size: A4 ${tipoRelatorio === 'checking' ? 'portrait' : 'landscape'}; margin: 5mm; }
        }
      `;
      document.head.appendChild(printStyles);
      
      // Adicionar ID para o relatório para facilitar a seleção no CSS
      const relatorioElement = document.getElementById('resultados-relatorio');
      if (relatorioElement) {
        relatorioElement.id = 'relatorio';
      }
      
      // Mostrar mensagem antes da impressão
      setSnackbar({
        open: true,
        message: 'Preparando para impressão. Selecione "Salvar como PDF" na janela de impressão.',
        severity: 'info',
      });
      
      // Pequeno delay para garantir que o snackbar seja exibido
      setTimeout(() => {
        window.print();
        
        // Restaurar o estilo original após a impressão
        document.body.style.cssText = originalBodyStyle;
        
        // Remover o estilo de impressão
        document.head.removeChild(printStyles);
        
        // Restaurar o ID original
        if (relatorioElement) {
          relatorioElement.id = 'resultados-relatorio';
        }
      }, 500);
      
    } catch (error) {
      console.error('Erro ao preparar impressão:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao preparar a impressão. Tente novamente.',
        severity: 'error',
      });
    }
  };
  
  // Função para exportar o relatório como Excel
  const exportToExcel = () => {
    if ((tipoRelatorio === 'checking' && registrosAgrupados.length === 0) ||
        (tipoRelatorio === 'exibicao' && registrosExibicao.length === 0)) {
      setSnackbar({
        open: true,
        message: 'Não há dados para exportar',
        severity: 'error',
      });
      return;
    }

    try {
      // Criar um novo workbook
      const wb = XLSX.utils.book_new();
      
      if (tipoRelatorio === 'checking') {
        // Para cada grupo de emissora, criar uma worksheet
        registrosAgrupados.forEach((grupo) => {
          // Preparar dados para a planilha
          const dados = grupo.registros.map((registro) => {
            const item = itensDisponiveis.find(i => i.id === registro.item_id);
            const programa = programasDisponiveis.find(p => p.id === registro.programa_id);
            const marca = marcasDisponiveis.find(m => m.id === registro.marca_id);
            
            return {
              'Data/Hora': formatDateTimeDisplay(registro.datahora),
              'Tipo': registro.tipo || '',
              'Item': item?.nome || '',
              'Marca': marca?.nome || '',
              'Título': registro.titulo || '',
              'Programa': programa?.nome || '',
              'Duração': formatDuration(registro.duracao),
              ...(mostrarCustos ? { 'Custo': formatCurrency(registro.custo) } : {})
            };
          });
          
          // Adicionar linha de totais
          dados.push({
            'Data/Hora': '',
            'Tipo': '',
            'Item': '',
            'Marca': '',
            'Título': '',
            'Programa': 'TOTAL',
            'Duração': formatDurationTotal(grupo.totalDuracao),
            ...(mostrarCustos ? { 'Custo': formatCurrency(grupo.totalCusto) } : {})
          });
          
          // Criar worksheet
          const ws = XLSX.utils.json_to_sheet(dados);
          
          // Adicionar worksheet ao workbook
          // Sanitizar o nome da emissora para remover caracteres inválidos em nomes de planilhas Excel
          const sheetName = grupo.emissoraNome.substring(0, 31).replace(/[:\\/?*\[\]]/g, '_');
          XLSX.utils.book_append_sheet(wb, ws, sheetName); // Limitar o nome a 31 caracteres (limite do Excel)
        });
        
        // Adicionar uma planilha para o total geral
        const dadosTotais = [{
          'Emissora': 'TOTAL GERAL',
          'Comerciais': totalComerciais,
          'Duração': formatDurationTotal(totalDuracao),
          ...(mostrarCustos ? { 'Custo': formatCurrency(totalCusto) } : {})
        }];
        
        const wsTotais = XLSX.utils.json_to_sheet(dadosTotais);
        XLSX.utils.book_append_sheet(wb, wsTotais, 'Total Geral');
      } else if (tipoRelatorio === 'exibicao') {
        // Criar uma única planilha para o relatório de exibição
        // Agrupar por emissora
        const emissorasUnicas = [...new Set(registrosExibicao.map(r => r.emissoraNome))];
        
        emissorasUnicas.forEach(emissoraNome => {
          const registrosDaEmissora = registrosExibicao.filter(r => r.emissoraNome === emissoraNome);
          
          // Criar cabeçalho com dias do mês
          const dadosExibicao: any[] = [];
          
          // Encontrar o período mais recente para criar cabeçalho dinâmico
          const periodos = [...new Set(registrosDaEmissora.map(r => r.periodo))];
          periodos.forEach(periodo => {
            const registrosDoPeriodo = registrosDaEmissora.filter(r => r.periodo === periodo);
            
            // Adicionar título do período
            dadosExibicao.push({
              'Programa': `PERÍODO: ${periodo}`,
              'Duração': '',
              'Custo Unitário': '',
              'Total Exibições': ''
            });
            
            // Extrair mês e ano do período (formato MM/YYYY)
            const [mes, ano] = periodo.split('/');
            // Determinar o número máximo de dias no mês
            const ultimoDiaDoMes = new Date(parseInt(ano), parseInt(mes), 0).getDate();
            
            // Criar um conjunto de todos os dias possíveis no mês (1 a 31 ou 30 etc.)
            const diasDoMes = Array.from({length: ultimoDiaDoMes}, (_, i) => i + 1);
            
            registrosDoPeriodo.forEach(registro => {
              const linha: any = {
                'Programa': registro.programaNome,
                'Duração': formatDuration(registro.duracao),
                'Custo Unitário': mostrarCustos ? formatCurrency(registro.custoUnitario) : '',
              };
              
              // Inicializar todos os dias do mês com 0
              diasDoMes.forEach(dia => {
                linha[`Dia ${dia}`] = 0;
              });
              
              // Preencher os dias que têm exibições
              Object.entries(registro.diasExibicao).forEach(([dia, quantidade]) => {
                linha[`Dia ${dia}`] = quantidade;
              });
              
              // Calcular o total como a soma de todas as exibições
              const totalExibicoes = Object.values(registro.diasExibicao).reduce((sum, count) => sum + count, 0);
              linha['Total Exibições'] = totalExibicoes;
              
              // Adicionar custo total se necessário
              if (mostrarCustos) {
                linha['Valor Total'] = formatCurrency(registro.custoUnitario * totalExibicoes);
              }
              
              dadosExibicao.push(linha);
            });
            
            // Adicionar linha em branco entre períodos
            dadosExibicao.push({});
          });
          
          // Criar worksheet
          const ws = XLSX.utils.json_to_sheet(dadosExibicao);
          
          // Adicionar worksheet ao workbook
          // Sanitizar o nome da emissora para remover caracteres inválidos em nomes de planilhas Excel
          const sheetName = emissoraNome.substring(0, 31).replace(/[:\\/?*\[\]]/g, '_');
          XLSX.utils.book_append_sheet(wb, ws, sheetName); // Limitar o nome a 31 caracteres
        });
      }
      
      // Gerar arquivo Excel e fazer download
      const nomeArquivo = `relatorio_${tipoRelatorio}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
      XLSX.writeFile(wb, nomeArquivo);
      
      setSnackbar({
        open: true,
        message: 'Relatório Excel gerado com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao gerar relatório Excel. Tente novamente.',
        severity: 'error',
      });
    }
  };

  const formatDateTimeDisplay = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds && seconds !== 0) return '';
    
    // Formatar como "30""
    return `${seconds}"`;
  };

  const formatCurrency = (valor?: number) => {
    if (!valor && valor !== 0) return '';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatDurationTotal = (seconds?: number) => {
    if (!seconds && seconds !== 0) return '';
    
    // Formatar como "2640""
    return `${seconds}"`;
  };

  return (
    <SidebarLayout>
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Relatórios
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Gere relatórios detalhados com base nos filtros aplicados.
          </Typography>
        </Box>

        {/* Filtros */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              Filtros
            </Typography>
            <Stack direction="row" spacing={1}>
              <FormControl size="small" sx={{ minWidth: 180, mr: 1 }}>
                <InputLabel id="tipo-relatorio-label">Tipo de Relatório</InputLabel>
                <Select
                  labelId="tipo-relatorio-label"
                  id="tipo-relatorio-select"
                  value={tipoRelatorio}
                  label="Tipo de Relatório"
                  onChange={(e) => setTipoRelatorio(e.target.value as TipoRelatorio)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="checking">Checking</MenuItem>
                  <MenuItem value="exibicao">Exibição</MenuItem>
                </Select>
              </FormControl>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleClearFilters}
                startIcon={<DataOffIcon />}
                sx={{ borderRadius: 2 }}
              >
                Limpar
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterIcon />}
                sx={{ borderRadius: 2 }}
              >
                {showFilters ? 'Menos filtros' : 'Mais filtros'}
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={2} alignItems="center">
            {/* Filtros principais */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                multiple
                options={emissorasDisponiveis}
                getOptionLabel={(option) => option.nome || ''}
                isOptionEqualToValue={(option, value) => option.id === (value?.id || 0)}
                value={filter.emissora_ids 
                  ? emissorasDisponiveis.filter(e => filter.emissora_ids && filter.emissora_ids.includes(e.id))
                  : filter.emissora_id 
                    ? [emissorasDisponiveis.find(e => e.id === filter.emissora_id)].filter(Boolean)
                    : []}
                onChange={(_, newValue) => {
                  // Remover o filtro antigo de emissora_id singular se existir
                  if (filter.emissora_id) {
                    handleFilterChange('emissora_id', undefined);
                  }
                  // Definir o novo array de emissora_ids
                  handleFilterChange('emissora_ids', newValue.length > 0 ? newValue.map(item => item?.id || 0).filter(id => id !== 0) : undefined);
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Emissoras" 
                    sx={{ borderRadius: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  if (!option) return null;
                  return (
                    <li {...props} key={option.id || 'unknown'}>
                      {option.nome || ''}
                      {option.cidade && option.estado ? 
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                          {option.cidade}/{option.estado}
                        </Typography> : null
                      }
                    </li>
                  );
                }}
                renderTags={(tagValue, getTagProps) =>
                  (tagValue || []).map((option, index) => {
                    const tagProps = getTagProps({ index });
                    // Criando um novo objeto para evitar propriedades duplicadas
                    const { key, ...restTagProps } = tagProps;
                    
                    return (
                      <Chip
                        key={option?.id || `emissora-${index}`}
                        variant="filled"
                        label={option?.nome || 'Emissora'}
                        size="small"
                        {...restTagProps}
                      />
                    );
                  })
                }
              />
            </Grid>
            
            {/* Filtros de Cidade e Estado */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                multiple
                options={cidadesDisponiveis}
                value={filter.cidades || []}
                onChange={(_, newValue) => {
                  handleFilterChange('cidades', newValue.length > 0 ? newValue : undefined);
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Cidades" 
                    sx={{ borderRadius: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    // Removendo a propriedade key do tagProps para evitar conflito
                    const { key, ...otherTagProps } = tagProps;
                    return (
                      <Chip
                        key={`cidade-${index}`}
                        variant="filled"
                        label={option}
                        size="small"
                        {...otherTagProps}
                      />
                    );
                  })
                }
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                multiple
                options={estadosDisponiveis}
                value={filter.estados || []}
                onChange={(_, newValue) => {
                  handleFilterChange('estados', newValue.length > 0 ? newValue : undefined);
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Estados" 
                    sx={{ borderRadius: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    // Removendo a propriedade key do tagProps para evitar conflito
                    const { key, ...otherTagProps } = tagProps;
                    return (
                      <Chip
                        key={`estado-${index}`}
                        variant="filled"
                        label={option}
                        size="small"
                        {...otherTagProps}
                      />
                    );
                  })
                }
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Título"
                variant="outlined"
                size="small"
                value={filter.titulo || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('titulo', e.target.value)}
                InputProps={{
                  sx: { borderRadius: 2 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Data início"
                type="date"
                value={filter.datahora_inicio || ''}
                onChange={(e) => handleFilterChange('datahora_inicio', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: { borderRadius: 2 }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Data fim"
                type="date"
                value={filter.datahora_fim || ''}
                onChange={(e) => handleFilterChange('datahora_fim', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: { borderRadius: 2 }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Duração de (s)"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={filter.duracao_min ?? ''}
                  onChange={(e) => handleFilterChange('duracao_min', e.target.value ? Number(e.target.value) : undefined)}
                  inputProps={{ min: 0 }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="até (s)"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={filter.duracao_max ?? ''}
                  onChange={(e) => handleFilterChange('duracao_max', e.target.value ? Number(e.target.value) : undefined)}
                  error={filter.duracao_min != null && filter.duracao_max != null && filter.duracao_max < filter.duracao_min}
                  inputProps={{ min: 0 }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="PI Number"
                variant="outlined"
                size="small"
                value={filter.pi_number || ''}
                onChange={(e) => handleFilterChange('pi_number', e.target.value || undefined)}
                InputProps={{
                  sx: { borderRadius: 2 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Filtros adicionais */}
            {showFilters && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={marcasDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={marcasDisponiveis.find(m => m.id === filter.marca_id) || null}
                    onChange={(_, newValue) => 
                      handleFilterChange('marca_id', newValue ? newValue.id : undefined)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Marca" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={itensDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={itensDisponiveis.find(i => i.id === filter.item_id) || null}
                    onChange={(_, newValue) => 
                      handleFilterChange('item_id', newValue ? newValue.id : undefined)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Item" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={setoresDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={setoresDisponiveis.find(s => s.id === filter.setor_id) || null}
                    onChange={(_, newValue) => 
                      handleFilterChange('setor_id', newValue ? newValue.id : undefined)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Setor" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={agenciasDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={agenciasDisponiveis.find(a => a.id === filter.agencia_id) || null}
                    onChange={(_, newValue) => 
                      handleFilterChange('agencia_id', newValue ? newValue.id : undefined)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Agência" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={anunciantesDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={anunciantesDisponiveis.find(a => a.id === filter.anunciante_id) || null}
                    onChange={(_, newValue) => 
                      handleFilterChange('anunciante_id', newValue ? newValue.id : undefined)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Anunciante" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={programasDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={programasDisponiveis.find(p => p.id === filter.programa_id) || null}
                    onChange={(_, newValue) => 
                      handleFilterChange('programa_id', newValue ? newValue.id : undefined)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Programa" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={categoriasDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={categoriasDisponiveis.find(c => c.id === filter.categoria_id) || null}
                    onChange={(_, newValue) => 
                      handleFilterChange('categoria_id', newValue ? newValue.id : undefined)}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Categoria" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={tiposDisponiveis}
                    getOptionLabel={(option) => option.nome || ''}
                    isOptionEqualToValue={(option, value) => option.id === (value as any).id || option.id === value}
                    value={filter.tipo ? tiposDisponiveis.find(t => t.id === filter.tipo) || null : null}
                    onChange={(_, newValue) => 
                      handleFilterChange('tipo', newValue ? newValue.id : '')}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Tipo" 
                        sx={{ borderRadius: 2 }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.nome}
                      </li>
                    )}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Observação (aparece no final do relatório/PDF)"
                placeholder="Ex.: comercial não localizado, alguma observação sobre este relatório..."
                value={observacaoRelatorio}
                onChange={(e) => setObservacaoRelatorio(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mostrarCustos}
                      onChange={(e) => setMostrarCustos(e.target.checked)}
                    />
                  }
                  label="Apresentar Custos"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agruparCampanhas}
                      onChange={(e) => setAgruparCampanhas(e.target.checked)}
                    />
                  }
                  label="Agrupar por campanha (PDF)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={incluirGraficos}
                      onChange={(e) => setIncluirGraficos(e.target.checked)}
                    />
                  }
                  label="Incluir gráficos (tela e PDF)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={incluirAudio}
                      onChange={(e) => setIncluirAudio(e.target.checked)}
                    />
                  }
                  label="Incluir áudio das inserções (rádio)"
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  px: 3,
                  py: 1,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                  }
                }}
              >
                Pesquisar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Botões para exportar - visíveis apenas quando há resultados */}
        {((tipoRelatorio === 'checking' && registrosAgrupados.length > 0) || 
          (tipoRelatorio === 'exibicao' && registrosExibicao.length > 0)) && !loading && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ExcelIcon />}
              onClick={exportToExcel}
              disabled={loading}
              sx={{
                borderRadius: 2,
                boxShadow: '0 4px 14px rgba(0,0,0,0.05)',
                px: 3,
                py: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                }
              }}
            >
              Exportar como Excel
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PdfIcon />}
              onClick={exportToPDF}
              disabled={loading}
              sx={{
                borderRadius: 2,
                boxShadow: '0 4px 14px rgba(0,0,0,0.05)',
                px: 3,
                py: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                }
              }}
            >
              Exportar como PDF
            </Button>
          </Box>
        )}

        {/* Resultados do Relatório */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : ((tipoRelatorio === 'checking' && registrosAgrupados.length === 0) || 
             (tipoRelatorio === 'exibicao' && registrosExibicao.length === 0)) ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              width: '100%',
              textAlign: 'center',
              py: 8
            }}
          >
            <Typography variant="h6" color="textSecondary">
              {filter.datahora_inicio || filter.titulo || filter.emissora_id ? 
                'Nenhum registro encontrado para os filtros aplicados' : 
                'Utilize os filtros acima e clique em Pesquisar para gerar o relatório'}
            </Typography>
          </Paper>
        ) : tipoRelatorio === 'checking' ? (
          <Box id="resultados-relatorio">
            {/* TELA: tabela de sempre (escondida no PDF) */}
            <Box className="checking-tela">
            {registrosAgrupados.map((grupo) => (
              <Paper 
                key={grupo.emissoraNome}
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 4,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  width: '100%',
                  borderLeft: `6px solid ${theme.palette.primary.main}`,
                  position: 'relative'
                }}
              >
                <Box sx={{ 
                  position: 'relative', 
                  mb: 3,
                  pb: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`
                }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: theme.palette.primary.main,
                      pl: 1
                    }}
                  >
                    Emissora: {grupo.emissoraNome}
                  </Typography>
                </Box>
                
                <TableContainer>
                  <Table sx={{ 
                    width: '100%',
                    [`& .${tableCellClasses.root}`]: {
                      borderBottom: '1px solid',
                      borderColor: 'rgba(0, 0, 0, 0.06)',
                    },
                    [`& .${tableCellClasses.head}`]: {
                      fontWeight: 'bold',
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                    },
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Hora</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Marca</TableCell>
                        <TableCell>Título</TableCell>
                        <TableCell>Programa</TableCell>
                        <TableCell>Duração</TableCell>
                        {mostrarCustos && <TableCell>Custo</TableCell>}
                        {temAudioDisponivel && <TableCell>Áudio</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grupo.registros.map((registro) => {
                        // Extrair data e hora do campo datahora
                        let data = '';
                        let hora = '';
                        
                        if (registro.datahora) {
                          const dataHoraFormatada = formatDateTimeDisplay(registro.datahora);
                          const partes = dataHoraFormatada.split(' ');
                          if (partes.length === 2) {
                            data = partes[0];
                            hora = partes[1];
                          }
                        }
                        
                        return (
                          <TableRow key={registro.id}>
                            <TableCell>{data}</TableCell>
                            <TableCell>{hora}</TableCell>
                            <TableCell>{registro.tipo || '-'}</TableCell>
                            <TableCell>{registro.item_id && itensDisponiveis?.find(i => i.id === registro.item_id)?.nome || '-'}</TableCell>
                            <TableCell>{registro.marca_id ? marcasDisponiveis.find(m => m.id === registro.marca_id)?.nome || '-' : '-'}</TableCell>
                            <TableCell>{registro.titulo || '-'}</TableCell>
                            <TableCell>
                              {registro.programa_id 
                                ? programasDisponiveis.find(p => p.id === registro.programa_id)?.nome || `ID: ${registro.programa_id}` 
                                : '-'
                              }
                            </TableCell>
                            <TableCell>{formatDuration(registro.duracao)}</TableCell>
                            {mostrarCustos && <TableCell>{formatCurrency(registro.custo)}</TableCell>}
                            {temAudioDisponivel && (
                              <TableCell>
                                {RegistrosService.urlAbsolutaAudio(audioLinks[String(registro.id)])
                                  ? (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      component="a"
                                      href={RegistrosService.urlAbsolutaAudio(audioLinks[String(registro.id)])}
                                      target="_blank"
                                      rel="noreferrer"
                                      sx={{ minWidth: 0, px: 1, py: 0.2, fontSize: '0.72rem' }}
                                    >
                                      ▶ ouvir
                                    </Button>
                                  )
                                  : <span style={{ color: '#9AA7AC' }}>—</span>}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableCell colSpan={7} sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                          Totais
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {`${grupo.totalComerciais} comerciais  `}
                          {formatDurationTotal(grupo.totalDuracao)}
                        </TableCell>
                        {mostrarCustos && (
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(grupo.totalCusto)}
                          </TableCell>
                        )}
                        {temAudioDisponivel && <TableCell />}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
            
            {/* Total Geral */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                width: '100%'
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                Total Geral
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Duração Total:</strong> {formatDurationTotal(totalDuracao)}
                  </Typography>
                </Grid>
                {mostrarCustos && (
                  <Grid item xs={12} md={12} sx={{ mt: 1 }}>
                    <Typography variant="body1">
                      <strong>Custo Total:</strong> {formatCurrency(totalCusto)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Gráficos na tela (mesmos do PDF) — quando "Incluir gráficos" está ligado */}
            {incluirGraficos && graficosRelatorio.dailyData.length > 0 && (
              <>
                <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', width: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                    Volume diário de inserções
                  </Typography>
                  <Box sx={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={graficosRelatorio.dailyData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E3EBEE" />
                        <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} />
                        <RechartsTooltip formatter={(v: number) => [`${v} inserç${v !== 1 ? 'ões' : 'ão'}`, 'Quantidade']} />
                        <Bar dataKey="count" name="Inserções" fill="#0E5870" radius={[4, 4, 0, 0]}>
                          <LabelList dataKey="count" position="top" style={{ fontSize: 11 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>

                <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', width: '100%' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                    Distribuição por horário
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={graficosRelatorio.hourlyData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E3EBEE" />
                        <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} />
                        <RechartsTooltip formatter={(v: number) => [`${v} inserç${v !== 1 ? 'ões' : 'ão'}`, 'Quantidade']} />
                        <Line type="monotone" dataKey="count" name="Inserções" stroke="#1C8FAE" strokeWidth={2} dot={{ r: 3, fill: '#1C8FAE' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </>
            )}
            </Box>
            {/* PDF: layout novo (escondido na tela, aparece só na impressão) */}
            <Box className="checking-pdf" sx={{ display: 'none' }}>
              <CheckingReport
                registros={registros}
                emissoras={emissorasDisponiveis}
                marcas={marcasDisponiveis}
                itens={itensDisponiveis}
                anunciantes={anunciantesDisponiveis}
                programas={programasDisponiveis}
                mostrarCustos={mostrarCustos}
                agruparPorCampanha={agruparCampanhas}
                observacao={observacaoRelatorio}
                incluirGraficos={incluirGraficos}
                audios={incluirAudio ? audioLinks : undefined}
              />
            </Box>
          </Box>
        ) : (
          // Relatório de Exibição
          <Box id="resultados-relatorio">
            {/* Cabeçalho com logo da CCP — escondido na tela, só aparece na impressão/PDF */}
            <Box className="relatorio-logo-print" sx={{
              display: 'none',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
              pb: 2,
              borderBottom: `2px solid ${theme.palette.primary.main}`
            }}>
              <Box
                component="img"
                src="/logo-ccp.png"
                alt="Controle Catarinense de Publicidade"
                sx={{ height: 64, maxWidth: '100%', objectFit: 'contain' }}
              />
            </Box>
            {/* Agrupar por emissora e período */}
            {registrosExibicao.length > 0 && (
              // Agrupar os registros por emissoraNome e periodo
              Object.entries(
                registrosExibicao.reduce<Record<string, Record<string, RegistroExibicao[]>>>((acc, registro) => {
                  if (!acc[registro.emissoraNome]) {
                    acc[registro.emissoraNome] = {};
                  }
                  if (!acc[registro.emissoraNome][registro.periodo]) {
                    acc[registro.emissoraNome][registro.periodo] = [];
                  }
                  acc[registro.emissoraNome][registro.periodo].push(registro);
                  return acc;
                }, {})
              )
                .sort(([emissoraA], [emissoraB]) => emissoraA.localeCompare(emissoraB))
                .map(([emissoraNome, periodos]) => (
                  <React.Fragment key={emissoraNome}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 3, 
                        mb: 4,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        width: '100%',
                        borderLeft: `6px solid ${theme.palette.primary.main}`,
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ 
                        position: 'relative', 
                        mb: 3,
                        pb: 2,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`
                      }}>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main,
                            pl: 1
                          }}
                        >
                          {emissoraNome}
                        </Typography>
                      </Box>

                      {/* Para cada período */}
                      {Object.entries(periodos)
                        .sort(([periodoA], [periodoB]) => periodoA.localeCompare(periodoB))
                        .map(([periodo, registrosPeriodo]) => {
                          // Calculando o total por dia (linha de Total)
                          const totalPorDia: Record<number, number> = {};
                          let totalGeral = 0;
                          
                          registrosPeriodo.forEach(registro => {
                            Object.entries(registro.diasExibicao).forEach(([dia, quantidade]) => {
                              const diaNum = parseInt(dia);
                              totalPorDia[diaNum] = (totalPorDia[diaNum] || 0) + quantidade;
                              totalGeral += quantidade;
                            });
                          });
                          
                          // Obter último dia do mês
                          const [mes, ano] = periodo.split('/');
                          const ultimoDia = new Date(parseInt(ano), parseInt(mes), 0).getDate();
                          
                          return (
                            <Box key={periodo} sx={{ mb: 4 }}>
                              <Typography variant="h6" sx={{ mb: 2 }}>
                                Período: {periodo}
                              </Typography>
                              
                              {/* Tabela com dias do mês como colunas */}
                              <TableContainer>
                                <Table size="small" sx={{ 
                                  [`& .${tableCellClasses.root}`]: {
                                    borderBottom: '1px solid',
                                    borderColor: 'rgba(0, 0, 0, 0.06)',
                                    padding: '6px 8px',
                                    fontSize: '0.75rem',
                                  },
                                  [`& .${tableCellClasses.head}`]: {
                                    fontWeight: 'bold',
                                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                                    textAlign: 'center',
                                  },
                                }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ minWidth: 180 }}>Programa</TableCell>
                                      {/* Colunas para cada dia do mês */}
                                      {Array.from({ length: ultimoDia }, (_, i) => i + 1).map(dia => (
                                        <TableCell key={dia} align="center" sx={{ minWidth: 24 }}>
                                          {dia}
                                        </TableCell>
                                      ))}
                                      <TableCell align="center">T</TableCell>
                                      <TableCell align="center">S"</TableCell>
                                      {mostrarCustos && <TableCell align="right">Custo Un.</TableCell>}
                                      {mostrarCustos && <TableCell align="right">Custo Tot.</TableCell>}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {/* Para cada programa */}
                                    {registrosPeriodo.map(registro => {
                                      return (
                                        <TableRow key={`${registro.programa_id}-${registro.programaNome}`}>
                                          <TableCell component="th" scope="row">
                                            {registro.programaNome}
                                          </TableCell>
                                          
                                          {/* Células para cada dia do mês */}
                                          {Array.from({ length: ultimoDia }, (_, i) => i + 1).map(dia => (
                                            <TableCell key={dia} align="center">
                                              {registro.diasExibicao[dia] || ''}
                                            </TableCell>
                                          ))}
                                          
                                          {/* Total */}
                                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                            {registro.total}
                                          </TableCell>
                                          
                                          {/* Duração */}
                                          <TableCell align="center">
                                            {formatDuration(registro.duracao)}
                                          </TableCell>
                                          
                                          {/* Custo unitário */}
                                          {mostrarCustos && (
                                            <TableCell align="right">
                                              {formatCurrency(registro.custoUnitario)}
                                            </TableCell>
                                          )}
                                          
                                          {/* Custo total */}
                                          {mostrarCustos && (
                                            <TableCell align="right">
                                              {formatCurrency(registro.custoUnitario * registro.total)}
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      );
                                    })}
                                    
                                    {/* Linha de Total */}
                                    <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                        Total
                                      </TableCell>
                                      
                                      {/* Totais por dia */}
                                      {Array.from({ length: ultimoDia }, (_, i) => i + 1).map(dia => (
                                        <TableCell key={dia} align="center" sx={{ fontWeight: 'bold' }}>
                                          {totalPorDia[dia] || 0}
                                        </TableCell>
                                      ))}
                                      
                                      {/* Total geral */}
                                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        {totalGeral}
                                      </TableCell>
                                      
                                      <TableCell></TableCell>
                                      
                                      {/* Custo total */}
                                      {mostrarCustos && <TableCell></TableCell>}
                                      
                                      {mostrarCustos && (
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                          {formatCurrency(
                                            registrosPeriodo.reduce((acc, reg) => 
                                              acc + (reg.custoUnitario * reg.total), 0)
                                          )}
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          );
                        })}
                    </Paper>
                  </React.Fragment>
                ))
            )}
            
            {/* Total Geral para relatório de exibição */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                width: '100%'
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                Total Geral
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Emissoras:</strong> {
                      // Contar emissoras únicas
                      new Set(registrosExibicao.map(r => r.emissoraNome)).size
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Comerciais:</strong> {
                      // Somar totais
                      registrosExibicao.reduce((acc, r) => acc + r.total, 0)
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">
                    <strong>Períodos:</strong> {periodosExibicao.length}
                  </Typography>
                </Grid>
                {mostrarCustos && (
                  <Grid item xs={12} md={12} sx={{ mt: 1 }}>
                    <Typography variant="body1">
                      <strong>Custo Total:</strong> {
                        formatCurrency(
                          registrosExibicao.reduce((acc, r) => acc + (r.custoUnitario * r.total), 0)
                        )
                      }
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        )}
      </Box>
      
      {/* Feedback de mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default RelatoriosPage;
